import {
  useEffect,
  useId,
  useRef,
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type PropsWithChildren
} from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n/useT';

interface SheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  /** False while a request is in flight: Close is hidden, Escape and backdrop taps are ignored. */
  dismissible?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Bottom sheet: a modal dialog that traps focus, closes on Escape and returns focus afterwards. */
export const Sheet = ({
  open,
  title,
  onClose,
  dismissible = true,
  children
}: PropsWithChildren<SheetProps>): JSX.Element | null => {
  const t = useT();
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    return () => {
      opener?.focus();
    };
  }, [open]);

  if (!open) {
    return null;
  }

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (dismissible) {
        onClose();
      }
      return;
    }
    if (event.key !== 'Tab' || dialog.current === null) {
      return;
    }
    const items = [...dialog.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = items[0];
    const last = items[items.length - 1];
    if (first === undefined || last === undefined) {
      // Nothing tabbable (a busy sheet): keep focus inside the dialog.
      event.preventDefault();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="ow-sheet-backdrop"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) {
          onClose();
        }
      }}
      role="presentation"
    >
      <div
        ref={dialog}
        className="ow-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={onKeyDown}
      >
        <div className="ow-sheet-head">
          <h2 id={titleId} className="ow-sheet-title">
            {title}
          </h2>
          {dismissible && (
            <button type="button" className="ow-btn-secondary ow-sheet-close" onClick={onClose}>
              {t('Close')}
            </button>
          )}
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};
